import { distinct, except } from "@entrilia/shared/utilities/arrayHelper";
import type { WorkflowDiagramStep } from "../../../../../api/types/workflowTypes";
import type { ConnectorPath, Edge, WorkflowGraph } from "./workflowDiagramTypes";

const getTopLevelStepIds = (workflowStepsMap: Record<string, WorkflowDiagramStep>): string[] => {
  const allChildIds: string[] = [];
  const allStepIds: string[] = [];

  Object.values(workflowStepsMap).forEach((step) => {
    step.childStepIds.forEach((childId) => allChildIds.push(childId));
    allStepIds.push(step.id);
  });

  return except(allStepIds, allChildIds);
};

const getEdgesToChildren = (sourceIds: string[], workflowMap: Record<string, WorkflowDiagramStep>): Edge[] => {
  const edges: Edge[] = [];

  sourceIds.forEach((sourceId) => {
    const step = workflowMap[sourceId];
    if (step) {
      step.childStepIds.forEach((targetId) => edges.push({ from: sourceId, to: targetId }));
    }
  });

  return edges;
};

export const getWorkflowGraph = (workflow: Record<string, WorkflowDiagramStep>): WorkflowGraph => {
  const stepIdsByIndex: string[][] = [];
  const edges: Edge[] = [];

  let currentLevelIds = getTopLevelStepIds(workflow);

  while (currentLevelIds.length > 0) {
    stepIdsByIndex.push(currentLevelIds);
    const childEdges = getEdgesToChildren(currentLevelIds, workflow);
    edges.push(...childEdges);
    currentLevelIds = distinct(childEdges.map((e) => e.to));
  }

  return { stepIdsByIndex, edges };
};

export const computeConnectorPaths = (
  edges: Edge[],
  nodeRefs: Record<string, HTMLDivElement>,
  containerRect: DOMRect
): ConnectorPath[] => {
  return edges
    .map(({ from, to }) => {
      const fromNode = nodeRefs[from];
      const toNode = nodeRefs[to];

      if (!fromNode || !toNode) {
        return undefined;
      }

      const fromRect = fromNode.getBoundingClientRect();
      const toRect = toNode.getBoundingClientRect();

      // start at the right center of `fromNode`
      const x1 = fromRect.right - containerRect.left;
      const y1 = fromRect.top + 22 - containerRect.top;

      // end at the left center of `toNode`
      const x2 = toRect.left - containerRect.left - 2;
      const y2 = toRect.top + 22 - containerRect.top;

      return { x1, y1, x2, y2 };
    })
    .filter(Boolean) as ConnectorPath[];
};
